/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { getTicket, clearTicket } from '../../../../actions/index';
import Loader from '../../../common/Loader';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import TicketVoid from './TicketVoid';
import TicketToolbar from './ticket-toolbar/TicketToolbar';
import { TICKET_PATH } from '../../../../config/paths';
import CreditNote from './CreditNote';
import {
  TICKET_BOARDING_STATUS,
  RELATIONSHIP,
  RELATIONSHIP_TO_MINOR,
  TICKET_STATUS,
  TICKET_SENSITIVE_TOOLBAR_STATUS,
} from '../../../../config/constants';
import Refund from './Refund';
import History from './History';
import {
  matchPropTypes,
  breadcrumbsPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import NoDataResource from '../../../common/resource/NoDataResource';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../../../config/locale';
import { numberFormatter } from '../../../../utils/number';
import EnumStatus from '../../../common/EnumStatus';
import TicketChangeHistoryComponent from './TicketChangeHistoryComponent';
import TicketDiscountCodeLabel from './TicketDiscountCodeLabel';
import TicketItineraryLabel from './TicketItineraryLabel';

const Ticket = ({
  breadcrumbs,
  dispatchGetTicket,
  dispatchClearTicket,
  ticket,
  match: {
    params: { id: ticketId },
  },
  activity,
  isForMine,
}) => {
  const [showExpenseModal, setShowExpenseModal] = React.useState(false);

  useEffect(() => {
    dispatchGetTicket({ ticketId });
    return () => {
      dispatchClearTicket();
    };
  }, [dispatchGetTicket, dispatchClearTicket, ticketId]);

  const getTransaction = (transactionList = []) => {
    if (transactionList.length === 0) {
      return null;
    }

    if (transactionList.length === 1) {
      return transactionList[0];
    }

    for (let i = transactionList.length - 1; i >= 0; i -= 1) {
      if (!transactionList[i].creditNoteId) {
        return transactionList[i];
      }
    }

    return transactionList.reduce((max, current) =>
      current.amount > max.amount ? current : max,
    );
  };

  const getUniquePaymentMethods = useCallback(
    (transactions) => {
      const paymentMethodsSet = new Set();
      const { manualPaymentMethod } = ticket.toJS();
      const originalTransaction = getTransaction(transactions);
      const replacedMethodId =
        manualPaymentMethod &&
        manualPaymentMethod.id !== originalTransaction.paymentMethod.id
          ? originalTransaction.paymentMethod.id
          : null;

      transactions.forEach((transaction) => {
        const method = transaction.paymentMethod;
        if (!method) return;

        if (replacedMethodId && method.id === replacedMethodId) return;

        paymentMethodsSet.add(method.name);
      });

      if (manualPaymentMethod) {
        paymentMethodsSet.add(manualPaymentMethod.name);
      }

      return [...paymentMethodsSet].join(', ');
    },
    [ticket],
  );

  let title = 'Boleto';
  let content;
  let toolbar = null;

  if (activity) {
    content = <Loader />;
  } else if (ticket.isEmpty()) {
    content = <NoDataResource returnPage={TICKET_PATH} />;
  } else {
    const ticketJson = ticket.toJS();

    const {
      ticketStatus: status,
      sourceLocation,
      destinationLocation,
      departureDate,
      seatReservation,
      nextTickets,
      previousTickets,
      customer,
      listPrice,
      ticketVoid,
      user,
      transactions,
      fullDocumentCode,
      documentAssignmentDate,
      contingencyMotive,
      contingencyAssignmentDocumentDate,
      itinerary,
      ticketExpense,
      customerUpdateHistory,
      boardingStatus,
      kidOnItineraryInfo,
      creditNoteList = [],
      boardingLocation,
      movementReasonName,
      ticketFreeOfCharge,
      proratedPayments,
    } = ticketJson;

    const firstTransaction =
      transactions && transactions.length > 0 ? transactions[0] : null;
    const voucherTypeId =
      firstTransaction && firstTransaction.voucherType
        ? firstTransaction.voucherType.id
        : null;
    const voucherType = firstTransaction ? firstTransaction.voucherType : null;
    const business = firstTransaction ? firstTransaction.business : null;

    const showHistory =
      (nextTickets && nextTickets.length > 0) ||
      (previousTickets && previousTickets.length > 0);

    const tripData = (
      <>
        <h3>Viaje</h3>
        <ResourceProperty label="Origen:">
          {sourceLocation.name}
        </ResourceProperty>
        <ResourceProperty label="Destino:">
          {destinationLocation.name}
        </ResourceProperty>
        <ResourceProperty label="Embarque:">
          {`${sourceLocation.name} - ${sourceLocation.address}`}
        </ResourceProperty>
        <ResourceProperty label="Desembarque:">
          {`${destinationLocation.name} - ${destinationLocation.address}`}
        </ResourceProperty>
        <ResourceProperty label="Fecha de Salida:">
          {departureDate
            ? tzNormalizeDate({
                date: departureDate,
                format: DATE_TIME_FORMAT,
              })
            : '-'}
        </ResourceProperty>
        <ResourceProperty label="Asiento:">
          {seatReservation ? seatReservation.seatNumber : '-'}
        </ResourceProperty>
        <ResourceProperty label="Piso:">
          {seatReservation ? seatReservation.floorNumber : '-'}
        </ResourceProperty>
        {itinerary && <TicketItineraryLabel itineraryId={itinerary.id} />}
      </>
    );

    const passengerData = (
      <>
        <h3>Pasajero</h3>
        <ResourceProperty label="Nombre Completo:">
          {customer.fullName}
        </ResourceProperty>
        <ResourceProperty label="Documento de identificación:">
          {`${customer.identificationType.name} ${customer.idDocumentNumber}`}
        </ResourceProperty>
        <ResourceProperty label="País:">
          {customer.idCountryOfOrigin}
        </ResourceProperty>
        <ResourceProperty label="Celular:">
          {customer.mobilePhone || '-'}
        </ResourceProperty>
        <ResourceProperty label="Email:">
          {customer.email || '-'}
        </ResourceProperty>
      </>
    );

    const relationShip = kidOnItineraryInfo && kidOnItineraryInfo.relationship;
    const seatNumber = kidOnItineraryInfo && kidOnItineraryInfo.seatNumber;
    const ticketNumber = kidOnItineraryInfo && kidOnItineraryInfo.ticketNumber;
    const notarizedPermit =
      kidOnItineraryInfo && kidOnItineraryInfo.notarizedPermit;
    const comments = kidOnItineraryInfo && kidOnItineraryInfo.comments;
    const parentTicketId = kidOnItineraryInfo && kidOnItineraryInfo.ticketId;
    let completeComments = comments;
    if (
      relationShip &&
      RELATIONSHIP_TO_MINOR[relationShip] ===
        RELATIONSHIP_TO_MINOR.TRAVELS_ALONE
    ) {
      completeComments = 'Viaja Solo';
    } else if (relationShip) {
      completeComments = `Viaja con "${RELATIONSHIP[relationShip].label}"`;
    }
    const kidInfo = (
      <>
        <h3>Observaciones</h3>
        <ResourceProperty label="Compañia:">
          {completeComments}
        </ResourceProperty>
        {seatNumber && (
          <ResourceProperty label="Asiento:">{seatNumber}</ResourceProperty>
        )}
        {ticketNumber && (
          <ResourceProperty label="Ref:">
            <a
              href={`${TICKET_PATH}/${parentTicketId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ticketNumber}
            </a>
          </ResourceProperty>
        )}
        {notarizedPermit && (
          <ResourceProperty label="PN:">{notarizedPermit}</ResourceProperty>
        )}
        {comments && (
          <ResourceProperty label="Comentarios:">{comments}</ResourceProperty>
        )}
      </>
    );

    const paymentData = !isForMine && (
      <>
        <h3>Pago</h3>
        <ResourceProperty label="Métodos de Pago:">
          {getUniquePaymentMethods(transactions)}
        </ResourceProperty>

        {transactions.length > 1 && (
          <>
            <h5 style={{ marginTop: '12px' }}>Detalle por método de Pago</h5>
            {proratedPayments &&
              proratedPayments.map((payment) => (
                <ResourceProperty
                  key={payment.paymentMethodId}
                  label={`${payment.paymentMethodName}:`}
                >
                  {numberFormatter({ value: payment.amount })}
                </ResourceProperty>
              ))}
          </>
        )}
        <TicketDiscountCodeLabel discountCode={ticketJson.discountCode} />
        <ResourceProperty label="Precio de Lista:">
          {listPrice && numberFormatter({ value: listPrice })}
        </ResourceProperty>
        <ResourceProperty label="Precio de Venta:">
          {numberFormatter({ value: ticketJson.salePrice })}
        </ResourceProperty>

        {transactions.some(
          (t) => t.paymentMethod.transactionCode === 'BANK_TRANSFER',
        ) &&
          ticketJson.bankAccount && (
            <>
              <h4>Transferencia Bancaria</h4>
              <ResourceProperty label="Banco:">
                {ticketJson.bankAccount.bank.name}
              </ResourceProperty>
              <ResourceProperty label="Número de Cuenta:">
                {ticketJson.bankAccount.accountNumber}
              </ResourceProperty>
              <ResourceProperty label="Número de Operación:">
                {ticketJson.bankOperationNumber}
              </ResourceProperty>
              <ResourceProperty label="Fecha de Depósito:">
                {tzNormalizeDate({
                  date: ticketJson.depositDate,
                  format: DATE_FORMAT,
                })}
              </ResourceProperty>
            </>
          )}
      </>
    );

    const businessData = business && (
      <>
        <h3>Factura</h3>
        <ResourceProperty label="RUC:">
          {business.businessTaxId}
        </ResourceProperty>
        <ResourceProperty label="Razón Social:">
          {business.name}
        </ResourceProperty>
      </>
    );

    const userData = (
      <>
        <h3>Usuario</h3>
        <ResourceProperty label="Nombre Completo:">
          {user.customer.fullName}
        </ResourceProperty>
      </>
    );

    const contingencyData =
      contingencyMotive !== null && contingencyAssignmentDocumentDate ? (
        <>
          <h3>Información de Contingencia</h3>
          <ResourceProperty label="Motivo de Contingencia:">
            {contingencyMotive}
          </ResourceProperty>
          <ResourceProperty label="Fecha de emisión de Contingencia:">
            {tzNormalizeDate({
              date: contingencyAssignmentDocumentDate,
              format: DATE_TIME_FORMAT,
            })}
          </ResourceProperty>
        </>
      ) : null;

    const ticketVoidComponent = ticketVoid && (
      <TicketVoid ticketVoid={ticketVoid} />
    );

    const creditNoteComponent =
      creditNoteList.length > 0 ? (
        <>
          {creditNoteList.map((creditNote) => (
            <CreditNote key={creditNote.id} creditNote={creditNote} />
          ))}
        </>
      ) : null;

    const refundComponent = status === TICKET_STATUS.REFUNDED.value && (
      <Refund />
    );

    const ticketChangeHistoryComponent = (
      <>
        <TicketChangeHistoryComponent ticket={ticket.toJS()} />
      </>
    );

    const expenseDetail = ticketExpense && (
      <Row className="mb-3">
        <Col lg={12} md={6}>
          <div className="d-flex justify-content-between py-2">
            <strong>Agencia:</strong>
            <span className="text-end">{ticketExpense.agencyName}</span>
          </div>
          <div className="d-flex justify-content-between py-2">
            <strong>Administrador Autorizador:</strong>
            <span className="text-end">{ticketExpense.authorizerUserName}</span>
          </div>
          <div className="d-flex justify-content-between py-2">
            <strong>Monto Entregado:</strong>
            <span className="text-end">
              {numberFormatter({ value: ticketExpense.total })}
            </span>
          </div>
        </Col>
      </Row>
    );

    const freeOfChargeData = ticketFreeOfCharge ? (
      <>
        <h3>Servicio a Título Gratuito</h3>
        <ResourceProperty label="Categoría:">
          {ticketFreeOfCharge.freeOfChargeCategoryName || '-'}
        </ResourceProperty>
        <ResourceProperty label="Subcategoría:">
          {ticketFreeOfCharge.freeOfChargeSubCategoryName || '-'}
        </ResourceProperty>
        <ResourceProperty label="Autorizador:">
          {ticketFreeOfCharge.authorizerUser
            ? ticketFreeOfCharge.authorizerUser.fullName
            : '-'}
        </ResourceProperty>
        <ResourceProperty label="Documento:">
          {ticketFreeOfCharge.authorizerUser
            ? `${
                ticketFreeOfCharge.authorizerUser.identificationTypeName || ''
              } ${
                ticketFreeOfCharge.authorizerUser.idDocumentNumber || ''
              }`.trim() || '-'
            : '-'}
        </ResourceProperty>
      </>
    ) : null;

    content = (
      <>
        <Row>
          <Col lg={6} md={12}>
            <ResourceProperty label="Tipo de Comprobante:">
              {voucherType ? voucherType.name : '-'}
            </ResourceProperty>
            <ResourceProperty label="Fecha de Emisión:">
              {documentAssignmentDate
                ? tzNormalizeDate({
                    date: documentAssignmentDate,
                    format: DATE_TIME_FORMAT,
                  })
                : null}
            </ResourceProperty>
            <ResourceProperty label="Estado Financiero:">
              <EnumStatus enumObject={TICKET_STATUS} enumValue={status} />
            </ResourceProperty>
            {ticketExpense && (
              <>
                <ResourceProperty label="Salida de Caja:">
                  <button
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowExpenseModal(true)}
                  >
                    {`${
                      ticketExpense.documentSeries
                    } - ${ticketExpense.documentCode
                      .toString()
                      .padStart(7, '0')}`}
                  </button>
                </ResourceProperty>
                <Modal
                  isOpen={showExpenseModal}
                  toggle={() => setShowExpenseModal(false)}
                  size="md"
                >
                  <ModalHeader>Detalles de salida</ModalHeader>
                  <ModalBody>{expenseDetail}</ModalBody>
                </Modal>
              </>
            )}
            <ResourceProperty label="Estado de Embarque:">
              <EnumStatus
                enumObject={TICKET_BOARDING_STATUS}
                enumValue={boardingStatus}
              />
            </ResourceProperty>
            <ResourceProperty label="Lugar de Embarque:">
              {boardingLocation ? boardingLocation.name : '-'}
            </ResourceProperty>
            <ResourceProperty label="Motivo:">
              {movementReasonName || '-'}
            </ResourceProperty>
            {tripData}
            {passengerData}
            {kidOnItineraryInfo && kidInfo}
            {paymentData}
            {freeOfChargeData}
            {businessData}
            {userData}
            {contingencyData}
          </Col>
          <Col lg={6} md={12}>
            {showHistory && (
              <History
                nextTickets={nextTickets}
                previousTickets={previousTickets}
              />
            )}
            {ticketVoidComponent}
            {creditNoteComponent}
            {refundComponent}
          </Col>
        </Row>
        {ticketChangeHistoryComponent}
      </>
    );

    const latestCreditNote =
      creditNoteList.length > 0 ? creditNoteList[0] : null;

    toolbar = (
      <TicketToolbar
        creditNote={latestCreditNote || null}
        ticketId={+ticketId}
        ticketStatus={status}
        voucherTypeId={voucherTypeId}
        departureDate={departureDate}
        availableCustomerUpdateHistory={Boolean(customerUpdateHistory)}
        disabledButtons={TICKET_SENSITIVE_TOOLBAR_STATUS.includes(
          boardingStatus,
        )}
        showPinPadVoucherButtons
      />
    );

    title = `Boleto ${fullDocumentCode || ''}`;
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      toolbar={toolbar}
      title={title}
      content={content}
    />
  );
};

Ticket.propTypes = {
  breadcrumbs: breadcrumbsPropTypes,
  dispatchGetTicket: PropTypes.func.isRequired,
  dispatchClearTicket: PropTypes.func.isRequired,
  ticket: PropTypes.instanceOf(Immutable.Map).isRequired,
  match: matchPropTypes.isRequired,
  activity: PropTypes.bool.isRequired,
  isForMine: PropTypes.bool.isRequired,
};

Ticket.defaultProps = {
  breadcrumbs: [],
};

const mapStateToProps = ({ ReservationUnit, authentication }) => ({
  breadcrumbs: [
    ...ReservationUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Boletos',
      href: TICKET_PATH,
    },
    {
      text: 'Ver',
      href: '',
    },
  ],
  ticket: ReservationUnit.Ticket.getIn(['current', 'content']),
  activity: !ReservationUnit.Ticket.getIn(['current', 'activity']).isEmpty(),
  isForMine: !!authentication.get('user').salesSessionUserId,
});

const mapDispatchToProps = {
  dispatchGetTicket: getTicket,
  dispatchClearTicket: clearTicket,
};

export default connect(mapStateToProps, mapDispatchToProps)(Ticket);
